/**
 * バックエンド（Python Cloud Functions → Cloud Run 移行）の呼び出し先を一元管理する切替スイッチボード。
 *
 * 役割
 * ----
 * フロントは従来、`https://asia-northeast1-<project>.cloudfunctions.net/<関数名>` という完全 URL を
 * 各ファイルに直書きしていた。これを `ep("<関数名>")` 経由に集約し、**1エンドポイント単位で
 * 旧 Functions / 新 Cloud Run をワンタッチ切替**できるようにする。これにより本番無影響の段階移行と
 * 即時ロールバックが成立する。
 *
 * 使い方
 * ------
 *   import { ep } from '../api/endpoints';
 *   const url = ep('get_crm_customers');   // 旧 or 新の完全 URL を返す
 *
 * 切替手順（移行時）
 * ------------------
 * 1. 新 Run へエンドポイントをデプロイし parity_check で旧と一致を確認する。
 * 2. その関数名を下の `MIGRATED` に1行追加する → 以降 `ep()` は `/api/<関数名>`（Hosting rewrite 経由で
 *    Cloud Run、同一オリジン）を返す。
 * 3. 問題が出たら `MIGRATED` から外すだけで即座に旧 Functions へ回帰する（他ファイルは触らない）。
 *
 * 注意
 * ----
 * - `MIGRATED` が空の間は全て旧 URL を返すため、本ファイル導入・URL 置換だけでは**振る舞いは一切変わらない**。
 * - Node 側関数（workspaceAssistant / listMyAgents / PDF テンプレート系 / obcProxy）は本移行の対象外。
 *   `ep()` に通さず従来 URL のまま扱うこと（`/api` rewrite の対象でもない）。
 */

/** 旧 Firebase Functions（Python codebase）のベース URL。 */
const LEGACY_BASE = 'https://asia-northeast1-plexiform-bot-455507-h3.cloudfunctions.net';

/** 新 Cloud Run のベース。Firebase Hosting の rewrite で `/api/**` → Cloud Run に転送される（同一オリジン）。 */
const API_BASE = '/api';

/**
 * 新 Cloud Run へ切り替え済みの関数名の集合。
 * parity_check 合格を確認した関数だけをここへ追加していく（初期は空＝全て旧 Functions のまま）。
 */
const MIGRATED: ReadonlySet<string> = new Set<string>([
  // parity 合格を確認した関数だけを追加して切り替える（旧 Functions とバイト一致を確認済み）。
  // --- 2026-06-25 初回切替 ---
  'get_touki_properties',        // 登記物件一覧
  // --- 2026-06-25 登記 詳細画面（parity 2/2 一致。PDF はバイナリ一致）---
  'get_touki_property_detail',   // 登記物件 詳細（物件+書類+表題部+権利部）
  'get_touki_document_pdf',      // 登記原本PDF（GCSから取得しインライン表示）
  // --- 2026-06-30 登記 対象マーク（新規エンドポイント。Cloud Run 直載せ＝旧 Functions に並行版なし）---
  'set_touki_watch_target',      // 登記物件の対象マーク 設定/解除（法務局 事件確認ウォッチ）
  // --- 2026-06-25 read系バッチ（parity_check 11/11 一致）---
  'get_staff_list',              // スタッフ一覧（MasterDataProvider）
  'get_branch_list',             // 支店一覧（MasterDataProvider）
  'get_department_list',         // 部門一覧（MasterDataProvider）
  'get_obc_trial_balance',       // 合計試算表（損益ダッシュボード）
  'get_active_budget_versions',  // 有効予算バージョン
  'get_crm_customers',           // CRM 顧客一覧
  'get_obc_accounts',            // OBC 勘定科目マスタ
  'get_obc_departments',         // OBC 部門マスタ
  'get_obc_sub_accounts',        // OBC 補助科目マスタ
  'get_salesforce_data',         // SFA 案件一覧
  'get_staff_headcount_trend',   // 人員数推移
  // --- 2026-06-26 合計試算表ドリルダウン（補助元帳。parity 2/2 一致・借方417,000実データ突合）---
  'get_obc_journal_details',     // 補助元帳 仕訳明細（合計試算表ドリルダウン）
  // --- 2026-06-26 経営概況詳細（損益）部門別損益（parity 2/2 一致）---
  'get_obc_pl_by_department',    // 部門別損益（経営概況詳細）
  // --- 2026-06-29 人事採用 計画入力（新規エンドポイント。Cloud Run 直載せ＝旧 Functions に並行版なし）---
  'get_recruitment_plan',        // 採用計画 参照（部門別 実績在籍＋計画フロー値）
  'get_cashflow',                // 資金繰り表（実績+予定マトリクス / セル明細。Cloud Run 直載せ）
  'upsert_recruitment_plan',     // 採用計画 一括upsert
  // --- 2026-06-25 GWS（フロント直呼びは calendar のみ。gws_sheets 等は Node→Cloud Run で別途配線）---
  'get_gws_calendar_events',     // カレンダー予定一覧（/api→api、api に GWS 鍵付与済み前提）
  // --- 2026-06-25 SFA/CRM 詳細画面の読取系（parity_check 9/9 一致。書込系は read-back 検証後に別途）---
  'get_sales_project_detail',    // SFA 案件詳細
  'get_activity_history',        // SFA 活動履歴
  'get_crm_customer_detail',     // CRM 顧客詳細
  'count_crm_employer_references', // CRM 勤務先参照件数
  'search_crm_customers',        // CRM 個人/法人 横断検索（SFA取引先選択）
  'search_crm_corporates',       // CRM 法人検索（Autocomplete）
  'check_duplicate_corporates',  // CRM 法人重複候補
  'check_duplicate_contacts',    // CRM 先方担当者重複候補
  'check_duplicate_individuals', // CRM 個人重複候補
  // --- 2026-06-25 SFA/CRM 詳細画面の書込系（エラー契約突合5/5一致＋新経路 no-op UPDATE 実証。コードは旧と同一）---
  'create_sales_project',        // SFA 案件 新規登録
  'update_sales_project',        // SFA 案件 更新
  'create_activity',             // SFA 活動履歴 新規登録
  'update_activity_history',     // SFA 活動履歴 更新
  'delete_activity_history',     // SFA 活動履歴 削除
  // --- 2026-07-03 案件種別分岐（新規エンドポイント。Cloud Run 直載せ＝旧 Functions に並行版なし）---
  'search_sfa_purchase_projects', // SFA 親案件（仕入案件）検索（売上案件フォームの親選択用）
  'upsert_crm_customer',         // CRM 顧客 登録/更新
  'detach_client_contact',       // CRM 先方担当者を一覧から外す
  // --- 2026-09-23 勤務先履歴（新規エンドポイント。Cloud Run 直載せ＝旧 Functions に並行版なし）---
  'get_crm_former_contacts',     // CRM 法人の元担当者（勤務先履歴の過去行）
  'update_crm_employment_history', // CRM 勤務先履歴の日付・メモ修正
  'delete_crm_employment_history', // CRM 勤務先履歴の過去行削除
  // --- 2026-07-01 ISO文書管理（新規エンドポイント。Cloud Run 直載せ＝旧 Functions に並行版なし）---
  'get_iso_standards',           // ISO 規格マスタ一覧
  'get_iso_clauses',             // ISO 規格条項ツリー（standard_id 指定）
  'get_iso_documents',           // 自社ISO文書 一覧
  'get_iso_document_detail',     // 自社ISO文書 詳細（文書+条項+改訂+紐付け）
  'search_iso_sections',         // 自社条項の検索（ILIKE + 規格条項紐付けJOIN）
  'get_iso_mapped_clauses',      // 条項紐付けの双方向照会
  // --- 2026-07-01 ISO 適用宣言書(SoA)ビュー ---
  'get_iso_soa',                 // SoA 管理策別行（附属書A + 適用可否/実施状況/対応規定）
  'upsert_iso_soa',              // SoA 意思決定 upsert（適用可否/理由/実施状況）
  // --- 2026-08-12 AI参照スコープ管理（新規エンドポイント。Cloud Run 直載せ＝旧 Functions に並行版なし）---
  'ai_scope_admin_overview',     // スコープカタログ + 全ユーザー付与一覧 (scope_admin 限定)
  'ai_scope_grant',              // スコープ付与 (scope_admin 限定)
  'ai_scope_revoke',             // スコープ剥奪 (scope_admin 限定)
  'ai_scope_view_definitions',   // スコープ定義の閲覧 (ビュー定義・公開/非公開カラム。scope_admin 限定)
  'ai_scope_view_columns',       // ビューの公開カラム構成の変更 (apply=false でプレビュー。scope_admin 限定)
  'ai_scope_view_history',       // カラム構成の変更履歴 (scope_admin 限定)
  // --- 2026-08-14 顧客管理 建物住所タブ（新規エンドポイント。Cloud Run 直載せ＝旧 Functions に並行版なし）---
  'get_crm_buildings',           // 建物一覧（所有者集約表示付き）
  'search_crm_buildings',        // 建物検索（顧客フォームの住所選択 Autocomplete）
  'get_crm_building_detail',     // 建物詳細（関係リンク + 登記リンク）
  'upsert_crm_building',         // 建物 登録/更新
  'upsert_building_relation',    // 建物と顧客の関係リンク 追加/編集
  'delete_building_relation',    // 関係リンク削除（誤登録訂正用）
  'set_building_touki_link',     // 建物と登記物件の紐付け 追加/解除
  'set_primary_building_relation', // メイン住居リンクの手動切替
  'set_project_building_link',   // 案件と建物のリンク 追加/解除
  // --- 2026-08-21 SFA 案件推進管理（新規エンドポイント。Cloud Run 直載せ＝旧 Functions に並行版なし）---
  'get_sfa_pipeline_summary',    // 推進残・受注残の断面集計（日付 x ランク、内訳軸）
  'get_sfa_pipeline_projects',   // 断面の案件明細（ドリルダウン）
  'get_sfa_phase_history',       // 案件単位のフェーズ変更履歴
  'get_sfa_lost_analysis',       // 失注分析（変更前フェーズ x 失注理由）
  'get_sfa_pipeline_masters',    // フェーズ確度マスタ・失注理由マスタ
  'run_sfa_pipeline_snapshot',   // 断面取得の手動実行（管理用）
  // --- 2026-10-09 推進案件機能統合 リリース1 案件構造（新規エンドポイント。Cloud Run 直載せ＝旧 Functions に並行版なし。設計 docs/推進案件機能統合_設計書.md）---
  'get_sfa_projects_v2',         // 案件一覧（タイプ・代表フェーズ・仕入状況・計上月・構成要約）
  'search_legacy_anken',         // 案件番号ミラー（Hozenall NET・工事管理）の検索（ミラーに無い 12 桁は Functions の 1 件照会を経由）
  'get_sfa_project_v2',          // 案件詳細（仕入契約/筆/履歴 + 販売区画/顧客/計上月 + 配分）
  'create_sfa_project_v2',       // 案件作成（区分選択なし。契約は任意、区画は必ず1つ）
  'update_sfa_project_v2',       // 案件本体の部分更新
  'upsert_sfa_contract',         // 仕入契約 登録/更新（タイプ判定と旧列書き戻し）
  'delete_sfa_contract',         // 仕入契約 削除
  'upsert_sfa_unit',             // 販売区画 登録/更新（顧客・フェーズ同時可）
  'delete_sfa_unit',             // 販売区画 削除
  'set_sfa_unit_customers',      // 区画の顧客（複数・代表者）置換
  'change_sfa_unit_phase',       // 区画フェーズ変更（受注=確定日必須、失注=理由必須）
  'change_sfa_contract_phase',   // 仕入フェーズ変更（売買契約=契約日必須、決済=決済日必須（未来日可）、失注=理由必須）
  'mark_sfa_project_lost',       // 案件を失注にする（未完了区画を同時失注）
  'upsert_sfa_parcels',          // 筆の登録/編集/区画割当
  'add_sfa_parcel_revision',     // 合筆・分筆・地積更正の登録
  'search_touki_candidates',     // 筆入力の登記物件候補検索
  // --- 2026-08-06 イベント管理（新規エンドポイント。Cloud Run 直載せ＝旧 Functions に並行版なし）---
  'get_event_management_data',   // イベント詳細 一覧一括取得（申込/参加者/入金/協賛）
  'get_event_list',              // イベント一覧（マスタ管理・申込サマリー付き）
  'upsert_event',                // イベントマスタ 登録/編集（event_code は新規時のみ）
  'update_event_payment',        // 入金消込（入金額の到達判定自動セット + is_paid 手動上書き）
  'update_event_sponsorship',    // 協賛品の部分更新（商品名/点数/日付/保管場所/受領/御礼状。全社員）
  'update_event_mail',           // 案内メール文面の保存（件名・本文。添付は Firebase Storage 直接管理）
  // send_event_invitee_mail は SMTPリレーのIP認証 (静的エグレスIP) が必要なため Cloud Run へ移行せず、
  // VPC コネクタ付きの旧 Functions 側で稼働する（MIGRATED に追加しないこと）
  'update_event_invitee',        // 配信予定 部分更新（案内希望フラグ・送付先・備考・plan_status）
  'add_event_invitee',           // 配信予定 追加（CRM 顧客選択。個人は勤務先も自動紐づけ）
  'delete_event_invitee',        // 配信予定 削除（行の除外）
  'request_delete_event_invitee', // 配信予定 削除依頼（実行委員会Chatへ通知。レコードは変更しない）
  'save_event_assignments',      // 配置（組合表/席次表/投順表）の一括保存（2026-09-24 新規・Cloud Run 直載せ）
  'add_event_staff_participants', // 当社社員を参加者に追加（社員配置用。Cloud Run 直載せ）
  'remove_event_staff_participant', // 社員の参加者行を配置ごと外す（Cloud Run 直載せ）
  // --- 2026-10-29 参加者の編集・お連れ様の追加/取消・顧客の紐付け（Cloud Run 直載せ）---
  'update_event_participant',    // 参加者の氏名・フリガナ・役職・メールの修正 + 個人顧客の紐付け
  'add_event_participant',       // お連れ様の追加（個人顧客の紐付け必須。人数・入金予定額を付け直す）
  'cancel_event_participant',    // お連れ様の取消（配置ごと削除。人数・入金予定額を付け直す）
  'cancel_event_entry',          // 申込の取消（欠席へ切替。参加者・配置を削除し、入金の記録が無ければ入金予定も外す）
  'find_event_participant_customers', // 紐付ける個人顧客と新規登録時の勤務先の候補
  // --- 2026-10-30 賞品の授与の編集・協賛品からの賞品枠作成（Cloud Run 直載せ）---
  'save_event_award',            // 賞品の授与 1 行の追加・更新（当選者未定の賞品枠も可）
  'delete_event_award',          // 賞品の授与 1 行の削除
  'create_event_award_slots',    // 協賛品から当選者未定の賞品枠を作る（点数から割当済みを引いた分）
  // --- 2026-11-03 賞品の授与の発表順のドラッグ並べ替え・賞品の画像（Cloud Run 直載せ）---
  'reorder_event_awards',        // 発表順をイベントの全行の新しい順で 1 から振り直す
  'upload_event_award_image',    // 賞品の画像を付ける（置き換え）
  'delete_event_award_image',    // 賞品の画像を外す
  // --- 2026-09-07 OBC 同期アラート通知先（新規エンドポイント。Cloud Run 直載せ）---
  'get_obc_alert_targets',       // 通知先一覧（社員 DM / スペース Webhook）
  'upsert_obc_alert_target',     // 通知先 登録/更新
  'delete_obc_alert_target',     // 通知先 削除
  'test_obc_alert_target',       // 通知先へテスト送信
  // --- 2026-09-07 為替・株式分析（新規エンドポイント。Cloud Run 直載せ。market_price_sync は Scheduler のため対象外）---
  'get_market_symbols',          // 銘柄一覧（蓄積状況付き）
  'upsert_market_symbol',        // 銘柄 登録/更新（新規は 5 年分バックフィル）
  'delete_market_symbol',        // 銘柄 削除（日足も CASCADE）
  'get_market_chart',            // 日足 + テクニカル指標 + サマリー
  'get_market_forecast',         // ARIMA 予測
  'run_market_sync',             // 日足取込の手動実行
  // --- 2026-09-14 顧客管理 顧客統合（新規エンドポイント。Cloud Run 直載せ）---
  'preview_crm_customer_merge',  // 統合プレビュー（付替件数・衝突項目）
  'merge_crm_customers',         // 統合実行
  // --- 2026-09-15 ターコイズ複数コード（新規エンドポイント。Cloud Run 直載せ）---
  'link_turquoise_match_to_customer', // 取込レビュー 候補なし行を既存顧客へ手動紐付け
  // --- 2026-09-16 建物住所マスタ 削除（新規エンドポイント。Cloud Run 直載せ）---
  'delete_crm_building',         // 建物削除（参照が残っていれば 400 で拒否）
  // --- 2026-09-16 顧客の添付ファイル（新規エンドポイント。Cloud Run 直載せ）---
  'register_crm_customer_asset', // 添付メタ登録（アップロード先パスを返す）
  'confirm_crm_customer_asset',  // アップロード完了（サイズ確定）
  'update_crm_customer_asset',   // ラベル変更
  'delete_crm_customer_asset',   // 削除（論理削除 + Storage 実体削除）
  // --- 2026-09-30 ターコイズミラー（新規エンドポイント。Cloud Run 直載せ）---
  'get_turquoise_sync_status',   // ミラー最終同期日時（一覧の鮮度表示用）
  // --- 2026-10-05 メール配信 (ブラストメール内製化。Cloud Run 直載せ＝旧 Functions に並行版なし) ---
  'mail_masters',                // 送信元・配信リスト・テンプレート
  'mail_template_save',          // テンプレート保存
  'mail_campaign_list',          // 配信履歴一覧
  'mail_campaign_get',           // 配信 1 件
  'mail_campaign_save',          // 下書き保存
  'mail_campaign_copy',          // 複製
  'mail_campaign_delete',        // 下書き削除
  'mail_campaign_preview',       // プレビュー HTML
  'mail_asset_upload',           // 本文画像のアップロード (公開バケット)
  'mail_campaign_recipients',    // 配信対象の件数
  'mail_campaign_deliveries',    // 宛先一覧
  'mail_campaign_send_test',     // テスト送信
  'mail_campaign_start',         // 配信開始 / 予約
  'mail_campaign_cancel',        // 予約取消
  'mail_list_members',           // 配信リストの読者一覧
  'mail_list_member_update',     // 読者の停止・再開・リストから外す・宛名修正
  'mail_list_member_add',        // 読者の追加
  // --- 2026-10-05 AI エージェント メール自動処理 (Cloud Run 直載せ) ---
  'mail_automation_status',      // 自動処理タブ: 監視状態・ルール・処理履歴
  'mail_automation_register',    // 自動処理タブ: 監視の再登録 (本人のみ)
  'mail_rule_save',              // 自動処理タブ: ルールの作成・更新
  'mail_rule_delete',            // 自動処理タブ: ルールの削除
  'mail_rule_test_candidates',   // 自動処理タブ: テスト投入の候補メール検索
  'mail_rule_test_run',          // 自動処理タブ: テスト投入 (会話と AI 実行)
]);

/**
 * 関数名からバックエンドの完全 URL を解決する。
 * `MIGRATED` に含まれていれば新 Cloud Run（`/api/<name>`）、そうでなければ旧 Functions（完全 URL）を返す。
 */
export const ep = (name: string): string =>
  MIGRATED.has(name) ? `${API_BASE}/${name}` : `${LEGACY_BASE}/${name}`;

export default ep;
